import History from '@history';
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import {
  createPronoGameApi,
  deletePronoGameApi,
  fetchPronoDrawApi,
  fetchPronoGameApi,
  fetchPronoGamesApi,
  fetchPronoParticipantsApi,
  fetchPronoStatsApi,
  runPronoDrawApi,
  setPronoParticipationApi,
  updatePronoGameApi,
} from '../api';
import {
  convertPronoFormToDTO,
  ParticipantsQuery,
  ParticipationOverride,
  PronoDrawRequestDTO,
  PronoUpdateDTO,
} from '../types';
import { currentPronoSelector } from './prono.selector';

const tr = (key: string) => i18n.t(`prono.${key}`, { ns: 'prono' });

export const fetchPronoGamesThunk = createAsyncThunk(
  'prono/fetchPronoGamesThunk',
  async ({ skip, limit }: { skip?: number; limit?: number }, { dispatch, rejectWithValue }) => {
    try {
      return await fetchPronoGamesApi(skip, limit);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('history.errorMessage'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const createPronoGameThunk = createAsyncThunk(
  'prono/createPronoGameThunk',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const { form } = currentPronoSelector(getState() as RootState);
      const payload = convertPronoFormToDTO(form);
      const created = await createPronoGameApi(payload);
      History.push(routesUrl.pronoHistory);
      dispatch(showMessage({ message: tr('creation.validation.success'), variant: 'success' }));
      return created;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('creation.validation.error'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const getPronoInfoThunk = createAsyncThunk(
  'prono/getPronoInfoThunk',
  async (gameId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      return await fetchPronoGameApi(gameId);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('detail.errorMessage'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const getPronoStatsThunk = createAsyncThunk(
  'prono/getPronoStatsThunk',
  async (gameId: EntityId, { rejectWithValue }) => {
    try {
      return await fetchPronoStatsApi(gameId);
    } catch (error: unknown) {
      return rejectWithValue(error);
    }
  },
);

export const fetchPronoParticipantsThunk = createAsyncThunk(
  'prono/fetchPronoParticipantsThunk',
  async ({ gameId, query }: { gameId: EntityId; query: ParticipantsQuery }, { dispatch, rejectWithValue }) => {
    try {
      return await fetchPronoParticipantsApi(gameId, query);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('detail.participants.errorMessage'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const getPronoDrawThunk = createAsyncThunk(
  'prono/getPronoDrawThunk',
  async (gameId: EntityId, { rejectWithValue }) => {
    try {
      // 404 if no draw has happened yet — expected behavior.
      return await fetchPronoDrawApi(gameId);
    } catch (error: unknown) {
      return rejectWithValue(error);
    }
  },
);

export const runPronoDrawThunk = createAsyncThunk(
  'prono/runPronoDrawThunk',
  async ({ gameId, payload }: { gameId: EntityId; payload: PronoDrawRequestDTO }, { dispatch, rejectWithValue }) => {
    try {
      const draw = await runPronoDrawApi(gameId, payload);
      dispatch(showMessage({ message: tr('detail.draw.success'), variant: 'success' }));
      return draw;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('detail.draw.error'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const updatePronoGameThunk = createAsyncThunk(
  'prono/updatePronoGameThunk',
  async ({ gameId, payload }: { gameId: EntityId; payload: PronoUpdateDTO }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await updatePronoGameApi(gameId, payload);
      dispatch(showMessage({ message: tr('detail.edit.success'), variant: 'success' }));
      return updated;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('detail.edit.error'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const setPronoParticipationThunk = createAsyncThunk(
  'prono/setPronoParticipationThunk',
  async (
    { gameId, override }: { gameId: EntityId; override: ParticipationOverride | null },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const updated = await setPronoParticipationApi(gameId, override);
      dispatch(showMessage({ message: tr('detail.participation.updateSuccess'), variant: 'success' }));
      return updated;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('detail.participation.updateError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const deletePronoThunk = createAsyncThunk(
  'prono/deletePronoThunk',
  async (gameId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      await deletePronoGameApi(gameId);
      dispatch(showMessage({ message: tr('history.deleteSuccess'), variant: 'success' }));
      return gameId;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('history.deleteError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);
